/**
 * Video Gallery (E20-1301)
 *
 * Playback is NOT here. theme.videoModal() already binds every anchor pointing at
 * youtube.com/watch, opens the global #VideoModal, and destroys the player on
 * close — so a card is just a link and the modal matches the rest of the site.
 *
 * This element covers only the two things that convention leaves open:
 *
 *   1. YouTube poster fallback. maxresdefault is a true 16:9 1280×720, but a video
 *      without one answers with a 120×90 grey placeholder at HTTP 200 — so onerror
 *      never fires and the natural size is the only tell. Two of the launch videos
 *      (Half Get Up, Swing) hit this. hqdefault always exists.
 *   2. Focus restore. Impulse's Modal.close() assigns `activeSource` the return
 *      value of setAttribute() (undefined), so its own restore never runs and
 *      keyboard focus is dropped to the top of the document on close.
 */
class VideoGallery extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.querySelectorAll('[data-poster-fallback]').forEach((img) => {
      // YouTube serves a 120×90 placeholder when the requested thumbnail
      // doesn't exist, so swap in the poster fallback when the loaded image is
      // that size — or when the thumbnail fails to load outright.
      const loaded = () => {
        if (img.naturalWidth <= 120 && img.naturalHeight <= 90) {
          img.src = img.dataset.posterFallback;
        }
        img.removeAttribute('data-poster-fallback');
      };
      const failed = () => {
        img.src = img.dataset.posterFallback;
        img.removeAttribute('data-poster-fallback');
      };
      if (img.complete) {
        if (img.naturalWidth === 0 && img.naturalHeight === 0) failed();
        else loaded();
      } else {
        img.addEventListener('load', loaded, { once: true, signal });
        img.addEventListener('error', failed, { once: true, signal });
      }
    });

    this.querySelectorAll('.bfx-video-gallery__card').forEach((card) => {
      card.addEventListener(
        'click',
        () => {
          this.activeCard = card;
        },
        { signal }
      );
    });

    document.addEventListener(
      'modalClose.VideoModal',
      () => {
        this.activeCard?.focus();
        this.activeCard = null;
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }
}

if (!customElements.get('video-gallery')) {
  customElements.define('video-gallery', VideoGallery);
}
